import { formatBusinessDuration } from "@/lib/businessDuration";
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  OPPORTUNITY_TIMING_SEGMENT_TYPE,
  type OpportunityTimingSegmentType,
  type OpportunityTimingSegment,
} from "@forgepoint/shared";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useOpportunitiesStore } from "../opportunitiesStore";

interface OpportunityTimingBarProps {
  opportunityId: string;
}

const SEGMENT_COLORS: Record<OpportunityTimingSegmentType, string> = {
  [OPPORTUNITY_TIMING_SEGMENT_TYPE.AGENT_WORKING]: "bg-info",
  [OPPORTUNITY_TIMING_SEGMENT_TYPE.WAITING_APPROVAL]: "bg-warning",
};

/**
 * Formats a duration in seconds into a human-readable string.
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g., "30s", "5m", "2.1h", "1.5d")
 */
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  }
  if (seconds < 86400) {
    return `${(seconds / 3600).toFixed(1)}h`;
  }
  return `${(seconds / 86400).toFixed(1)}d`;
}

/**
 * Calculates the total seconds across all segments for bar width calculation.
 * @param segments - The timing segments
 * @returns Total seconds across all segments
 */
function totalSegmentSeconds(segments: OpportunityTimingSegment[]): number {
  return segments.reduce((sum, s) => sum + s.durationSeconds, 0);
}

/**
 * Renders the skeleton loading placeholder for the timing bar.
 */
function TimingBarSkeleton() {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="h-3 w-24 rounded bg-muted animate-pulse" />
        <div className="h-3 w-16 rounded bg-muted animate-pulse" />
      </div>
      <div className="h-3 w-full rounded-full bg-muted animate-pulse" />
      <div className="flex gap-4">
        <div className="h-3 w-20 rounded bg-muted animate-pulse" />
        <div className="h-3 w-24 rounded bg-muted animate-pulse" />
      </div>
    </div>
  );
}

/**
 * Renders a single segment of the timing bar with tooltip and entrance animation.
 * @param segment - The timing segment data
 * @param totalSeconds - Total seconds across all segments for width calculation
 * @param index - Stagger index for entrance animation
 */
function TimingSegment({
  segment,
  totalSeconds,
  index,
}: {
  segment: OpportunityTimingSegment;
  totalSeconds: number;
  index: number;
}) {
  const widthPercent = (segment.durationSeconds / totalSeconds) * 100;
  const percent =
    Math.round((segment.durationSeconds / totalSeconds) * 1000) / 10;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            className={`${SEGMENT_COLORS[segment.type as OpportunityTimingSegmentType]} min-w-[4px] cursor-default`}
            style={{ width: `${widthPercent}%` }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{
              duration: 0.5,
              delay: index * 0.08,
              ease: [0.25, 1, 0.5, 1],
            }}
          />
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{segment.label}</p>
          <p>
            {formatDuration(segment.durationSeconds)} ({percent}%)
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Renders a stat row showing a label and formatted duration value.
 * @param label - Display label for the stat
 * @param value - Formatted duration to display
 * @returns The stat row
 */
function TimingStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * Displays a segmented horizontal bar visualizing how time was spent on an opportunity,
 * along with key timing metrics (time to quote, time to closed).
 * Segments: Agent Working (blue), Waiting for Approval (amber).
 * @param opportunityId - The ID of the opportunity to display timing for
 */
export function OpportunityTimingBar({
  opportunityId,
}: OpportunityTimingBarProps) {
  const fetchOpportunityTiming = useOpportunitiesStore(
    (state) => state.fetchOpportunityTiming,
  );
  const timing = useOpportunitiesStore((state) =>
    state.getOpportunityTiming(opportunityId),
  );
  const loadingState = useOpportunitiesStore((state) =>
    state.getOpportunityTimingLoadingState(opportunityId),
  );

  useEffect(() => {
    fetchOpportunityTiming(opportunityId);
  }, [opportunityId, fetchOpportunityTiming]);

  if (loadingState.isLoading) {
    return <TimingBarSkeleton />;
  }

  if (!timing) {
    return null;
  }

  const nonZeroSegments = timing.segments.filter(
    (segment) => segment.durationSeconds > 0,
  );
  const barTotalSeconds = totalSegmentSeconds(nonZeroSegments);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
    >
      <div className="space-y-1.5 mb-3">
        <TimingStat
          label="Time to Quote (Business)"
          value={formatBusinessDuration(timing.timeToQuoteBusinessSeconds)}
        />
        <TimingStat
          label="Time to Close (Business)"
          value={formatBusinessDuration(timing.timeToCloseBusinessSeconds)}
        />
      </div>

      {nonZeroSegments.length > 0 && barTotalSeconds > 0 && (
        <>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Time Breakdown
            </span>
          </div>

          <div
            className="w-full h-3 rounded-full overflow-hidden flex bg-muted/40"
            style={{ transformOrigin: "left" }}
          >
            {nonZeroSegments.map((segment, index) => (
              <TimingSegment
                key={segment.type}
                segment={segment}
                totalSeconds={barTotalSeconds}
                index={index}
              />
            ))}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
            {nonZeroSegments.map((segment) => (
              <div
                key={segment.type}
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <div
                  className={`w-2 h-2 rounded-full ${SEGMENT_COLORS[segment.type as OpportunityTimingSegmentType]}`}
                />
                <span>{segment.label}</span>
                <span className="font-medium text-foreground">
                  {formatDuration(segment.durationSeconds)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </motion.div>
  );
}
